import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaLock } from "react-icons/fa";
import { encryptData } from "../../funcs/encryption";

const shuffleStyle = `
  @keyframes shuffleWiggle {
    0% { transform: translate(0, 0) rotate(0deg); }
    25% { transform: translate(-5px, 5px) rotate(-3deg); }
    50% { transform: translate(5px, -5px) rotate(3deg); }
    75% { transform: translate(-3px, 3px) rotate(-2deg); }
    100% { transform: translate(0, 0) rotate(0deg); }
  }

  .shuffle-animation {
    animation: shuffleWiggle 1s ease-in-out;
  }

  .fade-points {
    transition: opacity 1s ease-out;
    opacity: 0;
  }
`;

/** Shuffle in-place */
const shuffleArray = (array) => {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

/** Sort descending by points (highest→lowest) */
const sortByPoints = (array) => {
  return [...array].sort((a, b) => {
    const aPoints = a.question?.points || a.customQuestion?.points || 0;
    const bPoints = b.question?.points || b.customQuestion?.points || 0;
    return bPoints - aPoints; // Descending order
  });
};

const ModesSection = ({ game, setGame }) => {
  const navigate = useNavigate();
  const { partnerSlug } = useParams();
  // Keep the /partner/:slug prefix when navigating within a partner page.
  const partnerBase = partnerSlug ? `/partner/${partnerSlug}` : "";

  const [showPoints, setShowPoints] = useState(true);
  const [isShuffling, setIsShuffling] = useState(false);

  // Identify the game code
  const gameCode = game?.codeInfo?.code || "";
  const shuffledKey = `shuffled_${gameCode}`;
  const fadedKey = `faded_${gameCode}`;

  // Check localStorage flags
  const hasShuffled = !!localStorage.getItem(shuffledKey);
  const hasFaded = !!localStorage.getItem(fadedKey);

  /**
   * Main effect controlling shuffle/fade once per game code.
   */
  useEffect(() => {
    if (!game || !gameCode) return;

    // If not fading questions => show points, no shuffle
    if (!game.fadedQuestions) {
      setShowPoints(true);
      setIsShuffling(false);
      return;
    }

    // If we've already done the fade => keep points hidden
    if (hasFaded) {
      setShowPoints(false);
      setIsShuffling(false);
      return;
    }

    // Otherwise, shuffle once if not done
    if (!hasShuffled) {
      const updatedGame = { ...game };
      updatedGame.modes = updatedGame.modes.map((mode) => {
        if (!mode?.questions) return mode;
        return { ...mode, questions: shuffleArray(mode.questions) };
      });

      // Store new game so parent sees it
      const enc = encryptData(updatedGame);
      localStorage.setItem("game", enc);
      setGame(updatedGame);

      localStorage.setItem(shuffledKey, "true");
    }

    // Now do wiggle => fade
    setShowPoints(true);
    setIsShuffling(true);

    const wiggleTimer = setTimeout(() => {
      setIsShuffling(false);
      const fadeTimer = setTimeout(() => {
        setShowPoints(false);
        localStorage.setItem(fadedKey, "true");
      }, 100);
      return () => clearTimeout(fadeTimer);
    }, 1000);

    return () => clearTimeout(wiggleTimer);
  }, [game, gameCode, setGame, hasShuffled, hasFaded, shuffledKey, fadedKey]);

  /**
   * finalModes processes the modes based on game settings
   */
  const finalModes = useMemo(() => {
    if (!game?.modes) return [];

    if (!game.fadedQuestions) {
      return game.modes.map((mode) => ({
        ...mode,
        questions: sortByPoints(mode.questions || []),
      }));
    }

    if (hasFaded) {
      return game.modes;
    }

    return game.modes;
  }, [game, hasFaded]);

  const isSingleMode = finalModes.length === 1;

  const renderSingleMode = () => {
    const mode = finalModes[0];
    const sortedQs = mode?.questions || [];
    const indexed = sortedQs.map((q, idx) => ({ q, idx }));
    const leftQs = indexed.filter(({ idx }) => idx % 2 === 0);
    const rightQs = indexed.filter(({ idx }) => idx % 2 !== 0);

    const lastUnansweredReversedIndex = [...sortedQs]
      .reverse()
      .findIndex(
        (x) => !(x?.question?.answered || x?.customQuestion?.answered),
      );
    const lastUnansweredIndex =
      lastUnansweredReversedIndex === -1
        ? -1
        : sortedQs.length - 1 - lastUnansweredReversedIndex;

    const renderCard = ({ q, idx }) => {
      const isAnswered = q?.question?.answered || q?.customQuestion?.answered;
      const shouldDisable =
        isAnswered ||
        (game?.lowToHighPoints && !isAnswered && idx !== lastUnansweredIndex);

      return (
        <div key={q._id} className="w-1/3 h-1/6 p-1 relative">
          <button
            onClick={() => navigate(`${partnerBase}/question/${q?._id}`)}
            disabled={shouldDisable}
            className={`
              h-full w-full bg-[#d1d3d4] rounded-lg overflow-hidden
              ${
                shouldDisable
                  ? game?.lowToHighPoints && !isAnswered
                    ? "bg-[#d1d3d4] text-[#8e8e8e] opacity-40"
                    : "bg-[#d6d9e1] text-[#8e8e8e] opacity-20"
                  : ""
              }
              ${isShuffling ? "shuffle-animation" : ""}
            `}
          >
            <div className="w-full h-full text-[#00009e] text-2xl md:text-3xl flex items-center justify-center font-sans font-semibold relative">
              <h1>
                {!showPoints && !isAnswered
                  ? idx + 1
                  : q?.question?.points || q?.customQuestion?.points}
              </h1>
              {game?.lowToHighPoints && !isAnswered && shouldDisable && (
                <div className="absolute w-8 h-8 flex justify-center items-center opacity-50">
                  <FaLock className="text-dark size-6" />
                </div>
              )}
            </div>
          </button>
        </div>
      );
    };

    return (
      <div className="flex w-full h-full p-1 lg:p-2 gap-1">
        <div className="w-[40%] h-full flex flex-row flex-wrap content-center">
          {leftQs.map(renderCard)}
        </div>
        <div
          className={`relative w-[20%] h-full flex items-center justify-center ${
            mode?.custom ? "border-4 border-mainText" : ""
          } overflow-hidden`}
        >
          <div className="h-full w-full flex flex-col">
            <img
              src={mode?.image}
              alt={mode?.name}
              className="h-[78%] w-full object-cover"
            />
            <div className="h-[22%] flex justify-center items-center bg-[#3B62CE] border-t-2 border-black">
              <h1 className="text-white text-xs sm:text-[0.5rem] lg:text-lg text-center font-sans font-bold">
                {mode?.name}
              </h1>
            </div>
          </div>
        </div>
        <div className="w-[40%] h-full flex flex-row flex-wrap content-center">
          {rightQs.map(renderCard)}
        </div>
      </div>
    );
  };

  return (
    <div className="h-[76vh] lg:h-[73vh] flex flex-row flex-wrap p-1 md:p-4">
      <style>{shuffleStyle}</style>

      {isSingleMode
        ? renderSingleMode()
        : finalModes.map((mode, index) => {
            // For layout purposes, we'll still partition by odd/even
            const sortedQuestions = [...mode.questions];
            const leftQuestions = sortedQuestions.filter(
              (q, idx) => idx % 2 === 0,
            );
            const rightQuestions = sortedQuestions.filter(
              (q, idx) => idx % 2 !== 0,
            );

            return (
              <div
                key={index}
                className="flex w-1/2 h-1/3 sm:w-1/3 sm:h-1/2 p-1 lg:p-2"
              >
                <div className="flex w-full h-full">
                  {/* Left Column */}
                  <div className="w-1/3 flex flex-col gap-1">
                    {leftQuestions.map((q, idx) => {
                      const isAnswered =
                        q?.question?.answered || q?.customQuestion?.answered;

                      let shouldDisable = isAnswered;
                      if (game?.lowToHighPoints && !isAnswered) {
                        // Find the lowest points unanswered question
                        const lastUnansweredReversedIndex = [...mode.questions]
                          .reverse()
                          .findIndex(
                            (x) =>
                              !(
                                x?.question?.answered ||
                                x?.customQuestion?.answered
                              ),
                          );

                        const lastUnansweredIndex =
                          lastUnansweredReversedIndex === -1
                            ? -1
                            : mode.questions.length -
                              1 -
                              lastUnansweredReversedIndex;

                        shouldDisable =
                          mode.questions.indexOf(q) !== lastUnansweredIndex;
                      }

                      return (
                        <button
                          onClick={() => navigate(`${partnerBase}/question/${q?._id}`)}
                          key={idx}
                          disabled={shouldDisable}
                          className={`
                        flex-1 bg-[#d1d3d4] rounded-bl-full rounded-tl-full overflow-hidden
                        ${
                          shouldDisable
                            ? game?.lowToHighPoints && !isAnswered
                              ? "bg-[#d1d3d4] text-[#8e8e8e] opacity-40"
                              : "bg-[#d6d9e1] text-[#8e8e8e] opacity-20"
                            : ""
                        }
                        ${isShuffling ? "shuffle-animation" : ""}
                      `}
                        >
                          <div className="w-full h-full text-[#00009e] text-2xl md:text-3xl flex items-center justify-center font-sans font-semibold relative">
                            <h1 className={`${!showPoints && isAnswered ? "fade-points" : ""}`}>
                              {!showPoints && !isAnswered
                                ? idx * 2 + 1
                                : q?.question?.points || q?.customQuestion?.points}
                            </h1>

                            {game?.lowToHighPoints &&
                              !isAnswered &&
                              shouldDisable && (
                                <div className="absolute w-8 h-8 flex justify-center items-center opacity-50">
                                  <FaLock className="text-dark size-6" />
                                </div>
                              )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Middle Column - Image */}
                  <div className="relative w-1/3 flex items-center justify-center">
                    <div
                      className={`w-full h-full ${
                        mode?.custom && "border-4 border-mainText"
                      } overflow-hidden`}
                    >
                      <div className="h-full">
                        <img
                          src={mode?.image}
                          alt={mode?.name}
                          className="h-[78%] w-full object-cover"
                        />
                        <div className="h-[22%] flex justify-center items-center bg-[#3B62CE] border-t-2 border-black">
                          <h1 className="text-white text-xs sm:text-[0.5rem] lg:text-lg text-center font-sans font-bold">
                            {mode?.name}
                          </h1>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="w-1/3 flex flex-col gap-1">
                    {rightQuestions.map((q, idx) => {
                      const isAnswered =
                        q?.question?.answered || q?.customQuestion?.answered;

                      let shouldDisable = isAnswered;
                      if (game?.lowToHighPoints && !isAnswered) {
                        const lastUnansweredReversedIndex = [...mode.questions]
                          .reverse()
                          .findIndex(
                            (x) =>
                              !(
                                x?.question?.answered ||
                                x?.customQuestion?.answered
                              ),
                          );

                        const lastUnansweredIndex =
                          lastUnansweredReversedIndex === -1
                            ? -1
                            : mode.questions.length -
                              1 -
                              lastUnansweredReversedIndex;

                        shouldDisable =
                          mode.questions.indexOf(q) !== lastUnansweredIndex;
                      }

                      return (
                        <button
                          onClick={() => navigate(`${partnerBase}/question/${q?._id}`)}
                          key={idx}
                          disabled={shouldDisable}
                          className={`
                        flex-1 bg-[#d1d3d4] rounded-br-full rounded-tr-full overflow-hidden
                        ${
                          shouldDisable
                            ? game?.lowToHighPoints && !isAnswered
                              ? "bg-[#d1d3d4] text-[#8e8e8e] opacity-40"
                              : "bg-[#d6d9e1] text-[#8e8e8e] opacity-20"
                            : ""
                        }
                        ${isShuffling ? "shuffle-animation" : ""}
                      `}
                        >
                          <div className="w-full h-full text-[#00009e] text-2xl md:text-3xl flex items-center justify-center font-sans font-semibold relative">
                            <h1>
                              {!showPoints && !isAnswered
                                ? idx * 2 + 2
                                : q?.question?.points || q?.customQuestion?.points}
                            </h1>

                            {game?.lowToHighPoints &&
                              !isAnswered &&
                              shouldDisable && (
                                <div className="absolute w-8 h-8 flex justify-center items-center opacity-50">
                                  <FaLock className="text-dark size-6" />
                                </div>
                              )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
    </div>
  );
};

export default ModesSection;
